import { Brain, Code, Scale, ArrowRight, CheckCircle2, Users, Target, TrendingUp, Sparkles, BarChart3, Shield } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
const Index = () => {
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handleStartDiagnosis = () => {
    navigate('/consultation');
  };
  const handleLearnMore = () => {
    scrollToSection('framework');
  };
  const handleConsultation = () => {
    navigate('/consultation');
  };
  const handleDownload = () => {
    navigate('/resources');
  };
  return <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20 dark:from-background dark:via-background dark:to-muted/10">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="hidden md:block">
                <span className="text-xl font-bold">KAILA</span>
                <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/about')} className="hidden md:flex">
                소개
              </Button>
              <Button variant="ghost" onClick={() => scrollToSection('framework')}>
                프레임워크
              </Button>
              <Button variant="ghost" onClick={() => navigate('/methodology')} className="hidden md:flex">
                방법론
              </Button>
              <Button variant="ghost" onClick={() => scrollToSection('roadmap')} className="hidden lg:flex">
                로드맵
              </Button>
              <Button variant="default" onClick={() => navigate('/consultation')} className="bg-blue-500 hover:bg-blue-600 text-white">
                상담신청
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 dark:from-blue-500/5 dark:via-transparent dark:to-purple-500/5" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.05),transparent_50%)]" />
        <div className="container mx-auto px-4 py-24 md:py-32 relative">
          <div className="max-w-5xl mx-auto text-center">
            <Badge className="mb-6 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 text-sm font-semibold">
              <Sparkles className="h-4 w-4 mr-2 inline" />
              KAILA Framework 2025
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent leading-tight">AI 리터러시 역량 진단 및 평가</h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
              기업의 AI 역량을 과학적으로 진단하고 측정하는 전문 프레임워크
            </p>
            
            {/* Quick Info */}
            <div className="flex flex-wrap justify-center gap-6 mb-12 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-muted-foreground">ISO/IEC 42001 기반</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-muted-foreground">12가지 핵심 지표</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-muted-foreground">8주 완료</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-6 text-lg shadow-lg" onClick={handleStartDiagnosis}>
상담 신청으로 <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="px-8 py-6 text-lg border-2" onClick={handleLearnMore}>
                자세히 알아보기
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-card border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center group">
              <div className="text-5xl font-bold bg-gradient-to-br from-blue-600 to-blue-400 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform">12</div>
              <div className="text-muted-foreground font-medium">핵심 지표</div>
            </div>
            <div className="text-center group">
              <div className="text-5xl font-bold bg-gradient-to-br from-purple-600 to-purple-400 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform">3</div>
              <div className="text-muted-foreground font-medium">진단 계층</div>
            </div>
            <div className="text-center group">
              <div className="text-5xl font-bold bg-gradient-to-br from-green-600 to-green-400 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform">5</div>
              <div className="text-muted-foreground font-medium">실행 단계</div>
            </div>
            <div className="text-center group">
              <div className="text-5xl font-bold bg-gradient-to-br from-orange-600 to-orange-400 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform">8</div>
              <div className="text-muted-foreground font-medium">주 완료</div>
            </div>
          </div>
        </div>
      </section>

      {/* 3-Layer Diagnosis Matrix */}
      <section id="framework" className="py-24 bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <Badge className="mb-4" variant="outline">진단 프레임워크</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">3 레이어 진단 매트릭스</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">KAILA의 12가지 핵심 지표를 기반으로 한 평가 범위</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Cognitive Layer */}
            <Card className="border-2 border-border hover:border-blue-500 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 dark:hover:shadow-blue-500/20 group bg-card/50 backdrop-blur">
              <CardHeader>
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <Brain className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold">이해 영역</CardTitle>
                <CardDescription className="text-base font-medium">(지식)</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 leading-relaxed">
                  <strong className="text-foreground">온라인 평가(50문항)</strong>를 통해 AI 기본 지식, 데이터 리터러시, 알고리즘 이해도를 평가합니다.
                </p>
                <div className="mt-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">AI 기본 개념 이해</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">데이터 리터러시</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">알고리즘 이해도</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Technical Layer */}
            <Card className="border-2 border-border hover:border-purple-500 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/10 dark:hover:shadow-purple-500/20 group bg-card/50 backdrop-blur">
              <CardHeader>
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <Code className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold">활용 능력</CardTitle>
                <CardDescription className="text-base font-medium">(수행)</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 leading-relaxed">
                  <strong className="text-foreground">실무 시뮬레이션(PBT)</strong>과 실제 업무를 통해 프롬프트 엔지니어링 및 도구 활용 능력을 평가합니다.
                </p>
                <div className="mt-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">프롬프트 엔지니어링</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">AI 도구 활용</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">실무 적용 능력</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Normative Layer */}
            <Card className="border-2 border-border hover:border-green-500 transition-all duration-300 hover:shadow-2xl hover:shadow-green-500/10 dark:hover:shadow-green-500/20 group bg-card/50 backdrop-blur">
              <CardHeader>
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <Scale className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold">윤리적 인식</CardTitle>
                <CardDescription className="text-base font-medium">(윤리)</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 leading-relaxed">
                  <strong className="text-foreground">시나리오 기반 인터뷰</strong>와 FGI를 통해 AI 윤리, 편향 감지, 보안 준수를 평가합니다.
                </p>
                <div className="mt-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">AI 윤리 의식</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">편향 감지 능력</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground/70">보안 준수</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 5-Phase Execution Pipeline */}
      <section id="process" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <Badge className="mb-4" variant="outline">실행 프로세스</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">5-단계 실행 절차</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">단계별 진단과 평가 프로세스</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="relative">
              {/* Connection Line */}
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-green-500/20 -translate-y-1/2 z-0" />
              
              <div className="grid md:grid-cols-5 gap-4 relative z-10">
                {/* Phase 1 */}
                <div className="bg-card rounded-xl p-6 border-2 border-blue-500 shadow-lg hover:shadow-2xl hover:shadow-blue-500/20 transition-all hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto group-hover:scale-110 transition-transform shadow-lg">
                    1
                  </div>
                  <h3 className="font-bold text-center mb-2">Scoping</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    맞춤화 및 KPI 설정
                  </p>
                </div>

                {/* Phase 2 */}
                <div className="bg-card rounded-xl p-6 border-2 border-purple-500 shadow-lg hover:shadow-2xl hover:shadow-purple-500/20 transition-all hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto group-hover:scale-110 transition-transform shadow-lg">
                    2
                  </div>
                  <h3 className="font-bold text-center mb-2">Online Assessment</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    정량적 설문조사
                  </p>
                </div>

                {/* Phase 3 */}
                <div className="bg-card rounded-xl p-6 border-2 border-green-500 shadow-lg hover:shadow-2xl hover:shadow-green-500/20 transition-all hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto group-hover:scale-110 transition-transform shadow-lg">
                    3
                  </div>
                  <h3 className="font-bold text-center mb-2">On Site Inspection</h3>
                  <p className="text-sm text-muted-foreground text-center">진단과 현장 실사</p>
                </div>

                {/* Phase 4 */}
                <div className="bg-card rounded-xl p-6 border-2 border-orange-500 shadow-lg hover:shadow-2xl hover:shadow-orange-500/20 transition-all hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto group-hover:scale-110 transition-transform shadow-lg">
                    4
                  </div>
                  <h3 className="font-bold text-center mb-2">Risk Analysis</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    갭 분석 및 프로파일링
                  </p>
                </div>

                {/* Phase 5 */}
                <div className="bg-card rounded-xl p-6 border-2 border-red-500 shadow-lg hover:shadow-2xl hover:shadow-red-500/20 transition-all hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto group-hover:scale-110 transition-transform shadow-lg">
                    5
                  </div>
                  <h3 className="font-bold text-center mb-2">Reporting</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    실행 계획 및 인증
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8-Week Implementation Roadmap */}
      <section id="roadmap" className="py-24 bg-gradient-to-b from-background to-muted/20">
        
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <Badge className="mb-4" variant="outline">핵심 가치</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Why KAILA Framework?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">과학적이고 체계적인 진단의 이점</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Target className="h-10 w-10 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-3">정확한 진단</h3>
              <p className="text-muted-foreground">12가지 핵심 지표 기반의 과학적 평가</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-3">맞춤형 솔루션</h3>
              <p className="text-muted-foreground">기업별 특성을 반영한 커스터마이징</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <TrendingUp className="h-10 w-10 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-3">실행 가능한 계획</h3>
              <p className="text-muted-foreground">즉시 적용 가능한 액션 플랜 제공</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <CheckCircle2 className="h-10 w-10 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-3">인증 및 검증</h3>
              <p className="text-muted-foreground">공식 인증을 통한 역량 검증</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
        <div className="container mx-auto px-4 text-center relative">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">기업의 AI 역량을 진단하고 향상시키세요</h2>
          <p className="text-xl md:text-2xl mb-12 opacity-90 max-w-3xl mx-auto">
            KAILA Framework로 체계적인 AI 리터러시 진단을 시작하세요
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-2xl" onClick={handleConsultation}>
              무료 상담 신청
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/20 px-8 py-6 text-lg" onClick={handleDownload}>
              자료 다운로드
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-12 mb-12">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-2xl font-bold">KAILA</span>
                </div>
                <p className="text-muted-foreground mb-4">Corporate AI Literacy Diagnosis Framework</p>
                <p className="text-sm text-muted-foreground">기업의 AI 역량을 과학적으로 진단하고 측정하는 전문 프레임워크</p>
              </div>
              <div>
                <h4 className="font-bold mb-4">프레임워크</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <button onClick={() => scrollToSection('framework')} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      진단 매트릭스
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('process')} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      실행 파이프라인
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('roadmap')} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      로드맵
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('framework')} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      방법론
                    </button>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4">문의</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <button onClick={handleConsultation} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      상담 신청
                    </button>
                  </li>
                  <li>
                    <button onClick={handleDownload} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      자료 요청
                    </button>
                  </li>
                  <li>
                    <button onClick={() => navigate('/partnership')} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      파트너십
                    </button>
                  </li>
                  <li>
                    <button onClick={() => {
                    toast({
                      title: "기술 지원",
                      description: "기술 지원 요청이 접수되었습니다. 지원팀에서 빠른 시일 내에 도와드리겠습니다."
                    });
                  }} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      지원
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <div className="border-t border-border pt-8 text-center text-sm text-muted-foreground space-y-2">
              <p>© 2026 KAILA  All rights reserved.</p>
              <button onClick={() => navigate('/admin/login')} className="text-xs hover:text-foreground transition-colors">
                관리자 로그인
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>;
};
export default Index;